import { ReactNode, useEffect, useState } from "react";

import AskAI from "@apps/ask-ai/ask-ai";
import { ChatSessionContext } from "@apps/ask-ai/ask-ai-context";
import useUiPreferenceCache, {
  UiPreferenceCache,
} from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";

import AskAiSidebarEmptyState from "./ask-ai-sidebar-empty-state";

// Shared Ask AI sidebar shell used by both the meeting page and the profile
// tabs. It owns the aside chrome (background, border, positioning), the Ask AI
// chat state, the "New chat" close header shown while a chat is active, and the
// Ask AI input pinned to the bottom via a flex-1 spacer.
//
// Callers pass any context-specific sections as `children` (e.g. a formal
// 1-on-1's My Notes / Coaching / Work History). When there are none, the shell
// renders the plain "empty" Ask AI sidebar. Editing this file updates both the
// profile and non-1-on-1 meeting sidebars at once.
const AskAiSidebar = ({
  sidebarExpandedUiPreferenceKey,
  resetKey,
  askAIEnabled,
  ariaLabel,
  meetingId,
  topClassName = "top-14",
  children,
}: {
  sidebarExpandedUiPreferenceKey: keyof UiPreferenceCache;
  // Resets Ask AI when it changes (a different meeting or profile).
  resetKey: number | string;
  askAIEnabled: boolean;
  ariaLabel: string;
  // Scopes Ask AI to a meeting when provided; omitted for a general chat.
  meetingId?: number;
  // Distance from the top of the viewport; profiles sit lower on mobile because
  // their header can wrap onto a second row.
  topClassName?: string;
  children?: ReactNode;
}) => {
  const { uiPreferenceCache } = useUiPreferenceCache();
  const expanded = !!uiPreferenceCache[sidebarExpandedUiPreferenceKey];

  const [askAIChatSessionId, setAskAIChatSessionId] = useState<null | number>(
    null,
  );
  const [startedChatSession, setStartedChatSession] = useState(false);
  const isChatActive = !!askAIChatSessionId || startedChatSession;

  useEffect(() => {
    // Reset Ask AI when the surface changes (different meeting or profile).
    setAskAIChatSessionId(null);
    setStartedChatSession(false);
  }, [resetKey]);

  const handleCloseChat = () => {
    setAskAIChatSessionId(null);
    setStartedChatSession(false);
  };

  if (!expanded) return null;

  return (
    <aside
      className={classNames(
        "w-96 xl:w-120 2xl:w-144 bg-[#FCFCFD]",
        "fixed right-0 bottom-0 z-rightSidebar",
        topClassName,
        "border-l tf-print-display-none",
        "flex flex-col overflow-hidden",
      )}
      aria-label={ariaLabel}
    >
      <ChatSessionContext.Provider value={{ meetingId }}>
        {isChatActive && (
          <div className="px-6 py-4 flex items-center justify-between shrink-0">
            <div className="tf-header-sm-dark">New chat</div>
            <Button
              icon={getTopicflowIcon("close")}
              onClick={handleCloseChat}
              theme={buttonTheme.iconGray}
              aria-label="Close chat"
            />
          </div>
        )}
        {!isChatActive &&
          (children ? (
            // Context sections (e.g. a 1-on-1's My Notes / Work History) start
            // at the top and scroll.
            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="px-6">{children}</div>
            </div>
          ) : askAIEnabled ? (
            // The empty Ask AI placeholder is centered in the open space.
            <div className="flex-1 min-h-0 flex flex-col justify-center px-6 py-6">
              <AskAiSidebarEmptyState />
            </div>
          ) : null)}
        {askAIEnabled && (
          <div
            className={classNames(
              isChatActive ? "flex-1 flex flex-col min-h-0" : "shrink-0",
            )}
          >
            <AskAI
              isInSidebar
              chatSessionId={askAIChatSessionId}
              onChangeChatSessionId={setAskAIChatSessionId}
              isFullScreen={isChatActive}
              className={classNames("px-6 pb-6", !isChatActive && "!pt-0")}
              totalYPaddingAroundContainer={110}
              onStartChatSession={() => setStartedChatSession(true)}
            />
          </div>
        )}
      </ChatSessionContext.Provider>
    </aside>
  );
};

export default AskAiSidebar;
